import type { Collection, IndexableType } from "dexie";
import { database } from "./connection";
import type {
	DatabaseProperties,
	EntityDatabaseType,
} from "./database-properties";
import { getCurrentISOStringWithTimezone } from "@simurgh/feathers/lib/date";
import type { Tables } from "./types";
import { generateId } from "./generate-id";

export function getTable<T extends DatabaseProperties, K extends IndexableType>(
	entity: Tables,
) {
	const table = database[entity] as unknown as EntityDatabaseType<T, K>;
	if (!table) {
		throw new Error(
			"Tags table not found. Ensure database schema migration (version bump) has been applied.",
		);
	}
	return table;
}

export function getTableRowById<
	T extends DatabaseProperties,
	K extends IndexableType,
>({ table, id }: { table: Tables; id: K }): Promise<T | undefined> {
	try {
		return getTable<T, K>(table).where("id").equals(id).first();
	} catch {
		return Promise.resolve(undefined);
	}
}

export function getTableRowsById<
	T extends DatabaseProperties,
	K extends IndexableType,
>({ table, ids }: { table: Tables; ids: K[] }): Promise<T[] | undefined> {
	try {
		return getTable<T, K>(table).where("id").anyOf(ids).toArray();
	} catch {
		return Promise.resolve(undefined);
	}
}

const LIMIT = 10;
export function paginateRecords<
	T extends DatabaseProperties,
	K extends IndexableType,
>(
	collection: Collection<T, K, T> | EntityDatabaseType<T, string>,
	page: number,
) {
	return collection.offset((page - 1) * LIMIT).limit(LIMIT);
}

export function generateDatabaseProperties({
	now = getCurrentISOStringWithTimezone(),
	table,
}: {
	now?: string;
	table: Tables;
}) {
	return {
		id: generateId({ table, createdAt: now }),
		createdAt: now,
		updatedAt: now,
	};
}
